import React from "react";

import { css, cx } from "@emotion/css";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { formatNumber } from "@/utils/mathUtils.ts";

const DEFAULT_PUMP_MODE_BARS_WIDTH_PX = 150;
const DEFAULT_PUMP_MODE_BARS_HEIGHT_PX = 20;

export type PumpModeStatItemProps = {
  title: string;
  percentage: number;
  percentagePrecision?: number;
  value?: number | string;
  precision?: number;
  unit?: string;
  className?: string;
  barWidth?: number;
  barHeight?: number;
  barColor?: string;
  barFillColor?: string;
};

export const PumpModeStatItem: React.FC<PumpModeStatItemProps> = ({
  title,
  percentage,
  value,
  percentagePrecision = 0,
  precision = 0,
  unit,
  barWidth = DEFAULT_PUMP_MODE_BARS_WIDTH_PX,
  barHeight = DEFAULT_PUMP_MODE_BARS_HEIGHT_PX,
  barColor = "grey",
  barFillColor = "grey",
  className,
}) => {
  const styles = useStyles(makeStyles);
  const svgContentWidth = barWidth - 4;
  const svgContentHeight = barHeight - 4;
  const svgContentOffset = 2;

  return (
    <div className={cx(styles.container, className)}>
      <div className={styles.barContainer}>
        <div className={styles.titleContainer}>
          <Typography variant="body" className={styles.title}>
            {title}
          </Typography>
        </div>
        <svg width={barWidth} height={barHeight}>
          <rect
            x={svgContentOffset}
            y={svgContentOffset}
            width={percentage < 0.01 ? 2 : svgContentWidth * percentage}
            height={svgContentHeight}
            stroke={barColor}
            fill={barFillColor}
            strokeWidth={2}
            rx={4}
            ry={4}
          />
        </svg>
        <div className={styles.values}>
          <Typography variant="subtitleLarge">{`${formatNumber(percentage * 100, percentagePrecision)}`}</Typography>
          <Typography variant="body">{"%"}</Typography>
          <Typography variant="bodySmall" className={styles.value}>
            {typeof value == "number" ? formatNumber(value, precision) : value}
            {`${unit ?? ""}`}
          </Typography>
        </div>
      </div>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
  `,
  barContainer: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(2),
  }),
  titleContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(2)};
    width: 7rem;
  `,
  title: css`
    flex: 1 1;
    white-space: nowrap;
    overflow: clip;
    text-overflow: ellipsis;
  `,
  values: css`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: ${theme.spacing(2)};
  `,
  value: css({
    marginLeft: theme.spacing(4),
  }),
});
